import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, FormattedNumber, injectIntl, intlShape } from "react-intl";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Alert, Badge, Button, Link, Modal, SelectField, Spinner, Title, Toggle } from "@yoast/ui-library";
import { noop } from "shared-frontend/functions/noop";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import { REFUND_POLICY_URL } from "../../../config/constants";
import { SubscriptionDetailContext } from "../../../context/SubscriptionDetailContext";
import formatAmount from "../../../functions/currency";
import useRequest from "../../../reactHooks/useRequest";
import useUpgradePrice from "../../../reactHooks/useUpgradePrice";
import { messages } from "./messages";
import * as styles from "./styles.module.scss";

/**
 * A modal for upgrading a subscription with paid prorated pricing.
 *
 * @param {Object} props The props as described by proptypes.
 *
 * @returns {JSX.Element} The PaidProratedUpgrade modal.
 */
const ProratedUpgradeModalComponent = ( props ) => {
	const { subscriptionId } = useContext( SubscriptionDetailContext );
	const [ selectedSwitchOptionIndex, setSelectedSwitchOptionIndex ] = useState( 0 );
	const [ refundPolicyChecked, setRefundPolicyChecked ] = useState( false );
	const [ upgradeError, setUpgradeError ] = useState( null );
	const [ checkoutURL, setCheckoutURL ] = useState( null );
	const [ isSubmitting, setIsSubmitting ] = useState( false );
	const canSubmit = refundPolicyChecked && ! isSubmitting;

	// Reset form state when modal closes
	useEffect( () => {
		if ( ! props.isOpen ) {
			setSelectedSwitchOptionIndex( 0 );
			setRefundPolicyChecked( false );
			setUpgradeError( null );
			setCheckoutURL( null );
			setIsSubmitting( false );
		}
	}, [ props.isOpen ] );

	const {
		data: switchOptions,
		isLoading,
		error: getSwitchOptionsError,
		reload,
	} = useRequest( `Subscriptions/${ subscriptionId }/product-switch-options?type=paid_prorated` );

	const selectedSwitchOption = switchOptions?.[ selectedSwitchOptionIndex ] ?? null;

	const {
		priceInCents: upgradePriceInCents,
		isLoading: isPriceLoading,
		error: priceError,
	} = useUpgradePrice(
		subscriptionId,
		selectedSwitchOption?.newProduct?.id,
		props.isOpen && Boolean( selectedSwitchOption ),
	);

	const handleSubmitButtonClick = useCallback( async() => {
		if ( ! canSubmit ) {
			return;
		}
		setIsSubmitting( true );
		setUpgradeError( null );
		setCheckoutURL( null );

		let newWindow = null;
		let windowOpened = false;

		const openWindow = () => {
			if ( ! windowOpened ) {
				windowOpened = true;
				newWindow = window.open( "/checkout-loading", "_blank" );
			}
		};

		// Delay opening the window slightly to reduce white page duration
		// Must stay within user gesture scope to avoid popup blockers
		const timeoutId = setTimeout( openWindow, 2000 );

		try {
			const response = await doRequest(
				prepareInternalRequest(
					`Subscriptions/${ subscriptionId }/create-upgrade-order`,
					"POST",
					{ productId: selectedSwitchOption.newProduct.id },
				),
			);

			// If request completes before timeout, open window immediately
			clearTimeout( timeoutId );
			openWindow();

			if ( response.checkoutURL && ( typeof URL.canParse !== "function" || URL.canParse( response.checkoutURL ) ) ) {
				if ( newWindow ) {
					newWindow.location.href = response.checkoutURL;
				} else {
					setCheckoutURL( response.checkoutURL );
					setUpgradeError( props.intl.formatMessage( messages.popupBlockedError ) );
				}
			} else {
				if ( newWindow ) {
					newWindow.close();
				}
				setUpgradeError( props.intl.formatMessage( messages.upgradeError ) );
			}
		} catch ( e ) {
			clearTimeout( timeoutId );
			if ( newWindow ) {
				newWindow.close();
			}
			const errorMessage = e?.response?.data?.message || e?.message || "An unexpected error occurred while creating your upgrade order.";
			console.error( "Error creating upgrade order: " + errorMessage, e );
			setUpgradeError( props.intl.formatMessage( messages.upgradeError ) );
		} finally {
			setIsSubmitting( false );
		}
	}, [ canSubmit, subscriptionId, selectedSwitchOption, props.intl ] );

	const upgradeRefundPolicyLink = useMemo(
		() => (
			<Link href={ REFUND_POLICY_URL } target="_blank">
				<FormattedMessage { ...messages.refundPolicyLabelLink } />
			</Link>
		),
		[],
	);

	if ( isLoading ) {
		return (
			<Modal
				isOpen={ props.isOpen }
				onClose={ props.onClose }
			>
				<Modal.Panel>
					<Modal.Container>
						<Modal.Container.Header className={ styles.modalHeader }>
							<Modal.Title size="2">
								<FormattedMessage { ...messages.modalHeader } />
							</Modal.Title>
						</Modal.Container.Header>
						<Modal.Container.Content className={ styles.modalContent }>
							<Spinner />
						</Modal.Container.Content>
					</Modal.Container>
				</Modal.Panel>
			</Modal>
		);
	}

	if ( getSwitchOptionsError ) {
		return (
			<Modal isOpen={ props.isOpen } onClose={ props.onClose }>
				<Modal.Panel>
					<Modal.Container>
						<Modal.Container.Header className={ styles.modalHeader }>
							<Modal.Title size="2">
								<FormattedMessage { ...messages.modalHeader } />
							</Modal.Title>
						</Modal.Container.Header>
						<Modal.Container.Content className={ styles.modalContent }>
							<p><FormattedMessage { ...messages.errorOccurredWhileFetching } /></p>
						</Modal.Container.Content>
						<Modal.Container.Footer className={ styles.modalFooter }>
							<div className={ styles.actionButtonsContainer }>
								{ props.onClose !== noop && (
									<Button variant="secondary" onClick={ props.onClose }>
										<FormattedMessage { ...messages.cancel } />
									</Button>
								) }
								<Button variant="secondary" onClick={ reload }>
									<FormattedMessage { ...messages.tryAgain } />
								</Button>
							</div>
						</Modal.Container.Footer>
					</Modal.Container>
				</Modal.Panel>
			</Modal>
		);
	}

	if ( ! switchOptions || switchOptions.length < 1 ) {
		return (
			<Modal isOpen={ props.isOpen } onClose={ props.onClose }>
				<Modal.Panel>
					<Modal.Container>
						<Modal.Container.Header className={ styles.modalHeader }>
							<Modal.Title size="2">
								<FormattedMessage { ...messages.modalHeader } />
							</Modal.Title>
						</Modal.Container.Header>
						<Modal.Container.Content className={ styles.modalContent }>
							<p><FormattedMessage { ...messages.noValidUpgrades } /></p>
						</Modal.Container.Content>
						<Modal.Container.Footer className={ styles.modalFooter }>
							<div className={ styles.actionButtonsContainer }>
								{ props.onClose && (
									<Button variant="secondary" onClick={ props.onClose }>
										<FormattedMessage { ...messages.close } />
									</Button>
								) }
							</div>
						</Modal.Container.Footer>
					</Modal.Container>
				</Modal.Panel>
			</Modal>
		);
	}

	return (
		<Modal isOpen={ props.isOpen } onClose={ props.onClose }>
			<Modal.Panel>
				<Modal.Container>
					<Modal.Container.Header className={ styles.modalHeader }>
						<Modal.Title size="2">
							<FormattedMessage { ...messages.modalHeader } />
						</Modal.Title>
					</Modal.Container.Header>
					<Modal.Container.Content className={ styles.modalContent }>
						<div className={ styles.upgradeTool }>
							<div className={ styles.section }>
								<Title as="h3" size="5">
									<FormattedMessage { ...messages.currentSubscription } />
								</Title>
								<div className={ styles.currentSubscription }>
									{ selectedSwitchOption.currentSubscription.product.name }
								</div>
							</div>

							<div className={ styles.section }>
								<SelectField
									id="upgrade-to"
									name="upgradeTo"
									label={ props.intl.formatMessage( messages.upgradeTo ) }
									value={ selectedSwitchOptionIndex.toString() }
									onChange={ setSelectedSwitchOptionIndex }
									options={ switchOptions.map( ( switchOption, key ) => ( {
										value: key.toString(),
										label: switchOption.newProduct.name,
									} ) ) }
								/>
							</div>

							<div className={ styles.section }>
								<Title as="h3" size="5">
									<FormattedMessage { ...messages.whatIsIncluded } />
								</Title>
								<div className={ styles.checkmarkList }>
									{ selectedSwitchOption.newProduct.productGroups.sort().map( ( productGroupName, index ) => (
										<div key={ `pg-${index}-${productGroupName}` }>
											<CheckCircleIcon />
											{ productGroupName }
										</div>
									) ) }
								</div>
							</div>

							<div className={ styles.section }>
								<Title as="h3" size="5">
									<FormattedMessage { ...messages.proratedUpgradePrice } />
								</Title>
								<p className="yst-mb-2.5">
									<FormattedMessage { ...messages.proratedUpgradePriceDescription } />
								</p>
								<div>
									{ priceError && (
										<Alert variant="error">
											<FormattedMessage { ...messages.errorLoadingPrice } />
										</Alert>
									) }
									{ isPriceLoading && (
										<span className="yst-flex yst-gap-1 yst-italic">
											<Spinner />
											<FormattedMessage { ...messages.calculatingPrice } />
										</span>
									) }
									{ ! isPriceLoading && upgradePriceInCents !== null && (
										<>
											<Badge as="div" variant="upsell" className={ styles.upgradePriceBadge }>
												{ /* $123.00 USD */ }
												<span>
													<FormattedNumber
														value={ formatAmount( upgradePriceInCents ) }
														currency={ selectedSwitchOption.newProduct.currency }
														style="currency"
													/>
													&nbsp;
													{ selectedSwitchOption.newProduct.currency }
												</span>
											</Badge>
											{ " " }
											<span className={ styles.priceAnnotation }>
												<FormattedMessage { ...messages.incVat } />
											</span>
										</>
									) }
								</div>
							</div>

							<div className={ styles.section }>
								<Title as="h3" size="5">
									<FormattedMessage { ...messages.nextRenewalPrice } />
								</Title>
								<p>
									<FormattedMessage { ...messages.nextRenewalPriceDescription } />
								</p>
								<div className={ styles.nextRenewalPrice }>
									<span className="yst-font-bold">
										{ /* $123.00 USD */ }
										<FormattedNumber
											value={ formatAmount( selectedSwitchOption.newEstimatedRenewalPriceInCents ) }
											currency={ selectedSwitchOption.newProduct.currency }
											style="currency"
										/>
										&nbsp;
										{ selectedSwitchOption.newProduct.currency }
									</span>
									<span className={ styles.priceAnnotation }>
										<FormattedMessage
											{ ...messages.nextBillingSubtext }
											values={ { term: selectedSwitchOption.newProduct.billingTerm } }
										/>
									</span>
								</div>
							</div>

							<div className={ styles.requiredCheckmarks }>
								<div className={ styles.toggleWrapper }>
									<Toggle
										id="switch-refund-policy"
										screenReaderLabel={ props.intl.formatMessage( messages.screenReaderRefundPolicyLabel ) }
										onChange={ setRefundPolicyChecked }
										checked={ refundPolicyChecked }
									/>
									<FormattedMessage
										{ ...messages.refundPolicyLabel }
										values={ { link: upgradeRefundPolicyLink } }
									/>
								</div>
							</div>

							{ upgradeError && (
								<Alert variant="error">
									{ upgradeError }
									{ checkoutURL && (
										<>
											{ " " }
											<Link href={ checkoutURL } target="_blank" rel="noopener noreferrer">
												<FormattedMessage { ...messages.clickHereToCheckout } />
											</Link>
										</>
									) }
								</Alert>
							) }
						</div>
					</Modal.Container.Content>
					<Modal.Container.Footer className={ styles.modalFooter }>
						<div className={ styles.actionButtonsContainer }>
							{ props.onClose && (
								<Button variant="secondary" onClick={ props.onClose }>
									<FormattedMessage { ...messages.cancel } />
								</Button>
							) }
							<Button
								variant="upsell"
								type="submit"
								onClick={ handleSubmitButtonClick }
								disabled={ ! canSubmit }
								isLoading={ isSubmitting }
							>
								<FormattedMessage { ...messages.proceedToCheckout } />
							</Button>
						</div>
					</Modal.Container.Footer>
				</Modal.Container>
			</Modal.Panel>
		</Modal>
	);
};

ProratedUpgradeModalComponent.propTypes = {
	intl: intlShape.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export const ProratedUpgradeModal = injectIntl( ProratedUpgradeModalComponent );
